import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/encaissements';

// Saisie d'un encaissement puis choix du canal : TPE (attente du terminal
// avec sondage toutes les 2 s) ou lien de paiement (SMS / e-mail).
// Ouvert vide (nouvel encaissement) ou sur un brouillon existant (on saute
// directement au choix du canal).

export const fmtEur = (cents) => (Number(cents || 0) / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
export const fmtDateHeure = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const FORM_VIDE = { concession_id: '', codsoc: '', client_code: '', client_nom: '', client_prenom: '', client_telephone: '', client_email: '', numero_facture: '', numero_bdc: '', montant_ttc: '', commentaire: '' };

function EncaissementModal({ user, contexte, societes, encaissement: initial, onClose, onChange }) {
  const [etape, setEtape] = useState(initial ? 'canal' : 'saisie');
  const [enc, setEnc] = useState(initial || null);
  const [form, setForm] = useState(FORM_VIDE);
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(false);
  const [terminaux, setTerminaux] = useState([]);
  const [terminalId, setTerminalId] = useState(() => localStorage.getItem('enc-terminal') || '');
  const [lienOpts, setLienOpts] = useState({ sms: true, email: true, validite_jours: contexte?.validite_lien_defaut || 7 });
  const [lienResultat, setLienResultat] = useState(null);
  const timer = useRef(null);
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'teleoperateur';

  // Concessions et sociétés proposées : dérivées de la liste des codsoc.
  const concessions = [];
  societes.forEach((s) => { if (!concessions.some((c) => c.id === s.concession_id)) concessions.push({ id: s.concession_id, nom: s.concession_nom, adyen_ok: s.adyen_ok }); });
  const concessionChoisie = Number(form.concession_id) || (concessions.length === 1 ? concessions[0].id : null);
  const societesDeLaConcession = societes.filter((s) => s.concession_id === concessionChoisie);

  useEffect(() => {
    if (!form.concession_id && concessions.length === 1) setForm((f) => ({ ...f, concession_id: String(concessions[0].id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societes]);
  useEffect(() => {
    if (societesDeLaConcession.length === 1 && form.codsoc !== societesDeLaConcession[0].codsoc) setForm((f) => ({ ...f, codsoc: societesDeLaConcession[0].codsoc }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concessionChoisie, societes]);

  const chargerTerminaux = useCallback(async (concessionId) => {
    try {
      const { data } = await api.terminaux(concessionId ? { concession_id: concessionId } : {});
      setTerminaux(data.terminaux || []);
      if (data.terminaux?.length === 1) setTerminalId(String(data.terminaux[0].id));
    } catch { setTerminaux([]); }
  }, []);
  useEffect(() => { if (etape === 'canal' && enc) chargerTerminaux(enc.concession_id); }, [etape, enc, chargerTerminaux]);

  // Sondage pendant l'attente du terminal.
  const sonder = useCallback(async (id) => {
    try {
      const { data } = await api.lire(id);
      setEnc(data.encaissement);
      onChange && onChange(data.encaissement);
      if (data.encaissement.statut !== 'en_attente_tpe') { setEtape('resultat'); return; }
      timer.current = setTimeout(() => sonder(id), 2000);
    } catch { timer.current = setTimeout(() => sonder(id), 4000); }
  }, [onChange]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const enregistrer = async (e) => {
    e.preventDefault();
    setErreur('');
    if (!form.numero_facture.trim() && !form.numero_bdc.trim()) return setErreur('Indiquez un numéro de facture ou de bon de commande.');
    setBusy(true);
    try {
      const { data } = await api.creer({ ...form, concession_id: concessionChoisie });
      setEnc(data.encaissement);
      onChange && onChange(data.encaissement);
      setEtape('canal');
    } catch (err) { setErreur(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };

  const lancerTpe = async () => {
    if (!terminalId) return setErreur('Choisissez un terminal.');
    setErreur(''); setBusy(true);
    try {
      localStorage.setItem('enc-terminal', terminalId);
      const { data } = await api.tpe(enc.id, Number(terminalId));
      setEnc(data.encaissement);
      onChange && onChange(data.encaissement);
      setEtape('attente');
      timer.current = setTimeout(() => sonder(enc.id), 2000);
    } catch (err) { setErreur(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };

  const annulerTpe = async () => {
    setBusy(true);
    try { await api.annuler(enc.id); } catch (err) { setErreur(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };

  const envoyerLien = async () => {
    setErreur(''); setBusy(true);
    try {
      const { data } = await api.lien(enc.id, lienOpts);
      setEnc(data.encaissement); setLienResultat(data);
      onChange && onChange(data.encaissement);
      setEtape('lien_ok');
    } catch (err) { setErreur(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };

  const copier = (txt) => { try { navigator.clipboard.writeText(txt); } catch { /* navigateur sans presse-papier */ } };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const refLibelle = enc ? (enc.numero_bdc ? `BDC ${enc.numero_bdc}` : `Facture ${enc.numero_facture}`) : '';
  const terminalChoisi = terminaux.find((t) => String(t.id) === String(terminalId));
  const adyenOk = concessions.find((c) => c.id === concessionChoisie)?.adyen_ok;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{etape === 'saisie' ? '💳 Nouvel encaissement' : `💳 ${enc?.reference} — ${fmtEur(enc?.montant_cents)}`}</h2>
          {etape !== 'attente' && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>

        {etape === 'saisie' && (
          <form onSubmit={enregistrer}>
            <div className="modal-body">
              {!societes.length && (
                <div className="status-warning enc-bandeau">Aucune société (codsoc) n'est rattachée à votre agence : demandez à un administrateur de renseigner les codes externes de la concession.</div>
              )}
              <div className="enc-form-grid">
                {(isSuperAdmin || concessions.length > 1) && (
                  <div>
                    <label>Agence</label>
                    <select value={form.concession_id} onChange={set('concession_id')} required>
                      <option value="">— Choisir —</option>
                      {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}{c.adyen_ok ? '' : ' (sans compte Adyen)'}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label>Société (facturation)</label>
                  <select value={form.codsoc} onChange={set('codsoc')} required disabled={societesDeLaConcession.length <= 1}>
                    {societesDeLaConcession.length !== 1 && <option value="">— Choisir —</option>}
                    {societesDeLaConcession.map((s) => <option key={s.codsoc} value={s.codsoc}>{s.codsoc} — {s.nom}</option>)}
                  </select>
                  <small className="enc-hint">La société part dans Adyen (metadata) : c'est elle que la comptabilité retrouve.</small>
                </div>
                {concessionChoisie && adyenOk === false && (
                  <div className="full status-warning enc-bandeau">Cette agence n'a pas encore de merchant account Adyen : l'encaissement pourra être saisi mais ni envoyé sur un TPE ni par lien (voir Configuration).</div>
                )}
                <div><label>Nom du client *</label><input value={form.client_nom} onChange={set('client_nom')} required maxLength={100} /></div>
                <div><label>Prénom</label><input value={form.client_prenom} onChange={set('client_prenom')} maxLength={100} /></div>
                <div>
                  <label>Code client (Datacar)</label>
                  <input value={form.client_code} onChange={set('client_code')} maxLength={100} placeholder="ex. 615986" />
                  <small className="enc-hint">Facultatif. Devient la référence client (C + société + code) visible dans Adyen.</small>
                </div>
                <div><label>Téléphone mobile</label><input value={form.client_telephone} onChange={set('client_telephone')} maxLength={30} placeholder="06…" /></div>
                <div className="full"><label>E-mail</label><input type="email" value={form.client_email} onChange={set('client_email')} maxLength={255} /></div>
                <div><label>N° de facture</label><input value={form.numero_facture} onChange={set('numero_facture')} maxLength={100} placeholder="FNC0000072" /></div>
                <div><label>N° de bon de commande</label><input value={form.numero_bdc} onChange={set('numero_bdc')} maxLength={100} /></div>
                <div className="full"><small className="enc-hint">Au moins l'un des deux. Le numéro part dans Adyen tel quel : saisissez-le comme sur le document.</small></div>
                <div>
                  <label>Montant TTC (€) *</label>
                  <input value={form.montant_ttc} onChange={set('montant_ttc')} required inputMode="decimal" placeholder="1 500,00" />
                </div>
                <div className="full"><label>Commentaire interne</label><input value={form.commentaire} onChange={set('commentaire')} maxLength={500} /></div>
              </div>
              {erreur && <div className="status-error enc-bandeau" style={{ marginTop: 12 }}>{erreur}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-primary" disabled={busy || !societes.length}>Continuer →</button>
            </div>
          </form>
        )}

        {etape === 'canal' && enc && (
          <>
            <div className="modal-body">
              <p style={{ margin: '0 0 4px' }}><b>{enc.client_prenom} {enc.client_nom}</b>{enc.client_code ? ` · ${enc.client_code}` : ''} — {refLibelle} — société {enc.codsoc}</p>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>Comment le client règle-t-il ?</p>
              <div className="enc-canaux">
                <div className="enc-canal" role="group">
                  <b>🖥️ Sur le TPE</b>
                  <small>Le montant s'affiche sur le terminal de l'agence : le client présente sa carte.</small>
                  <div className="enc-canal-options">
                    {terminaux.length === 0 ? (
                      <span className="status-warning enc-bandeau">Aucun terminal déclaré pour cette agence : le lien de paiement reste possible.</span>
                    ) : (
                      <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                        <option value="">— Terminal —</option>
                        {terminaux.map((t) => <option key={t.id} value={t.id}>{t.libelle} ({t.poiid}){t.occupe ? ' — occupé' : ''}</option>)}
                      </select>
                    )}
                    <button className="btn-primary" disabled={busy || !terminalId || !contexte?.adyen_configure} onClick={lancerTpe}>
                      Envoyer {fmtEur(enc.montant_cents)} sur le TPE
                    </button>
                    {contexte?.faux_terminal && <small>⚠️ Faux terminal (sandbox) : centimes .07 = refus, .09 = annulation client, .08 = aucune réponse.</small>}
                  </div>
                </div>
                <div className="enc-canal" role="group">
                  <b>🔗 Lien de paiement</b>
                  <small>Le client n'est pas sur place : il reçoit un lien Adyen sécurisé et paie par carte.</small>
                  <div className="enc-canal-options">
                    <label><input type="checkbox" checked={lienOpts.sms} disabled={!enc.client_telephone} onChange={(e) => setLienOpts((o) => ({ ...o, sms: e.target.checked }))} /> SMS {enc.client_telephone ? `au ${enc.client_telephone}` : '(pas de téléphone)'}</label>
                    <label><input type="checkbox" checked={lienOpts.email} disabled={!enc.client_email} onChange={(e) => setLienOpts((o) => ({ ...o, email: e.target.checked }))} /> E-mail {enc.client_email ? `à ${enc.client_email}` : '(pas d\'e-mail)'}</label>
                    <label>Valable <input type="number" min={1} max={contexte?.validite_lien_max || 70} value={lienOpts.validite_jours} onChange={(e) => setLienOpts((o) => ({ ...o, validite_jours: e.target.value }))} style={{ width: 60, padding: 4, borderRadius: 6, border: '1px solid var(--border-default)' }} /> jours</label>
                    <button className="btn-primary" disabled={busy || (!enc.client_telephone && !enc.client_email) || !contexte?.adyen_configure} onClick={envoyerLien}>
                      Créer et envoyer le lien
                    </button>
                  </div>
                </div>
              </div>
              {!contexte?.adyen_configure && <div className="status-warning enc-bandeau" style={{ marginTop: 12 }}>Adyen n'est pas configuré sur cette instance (ADYEN_API_KEY) : l'encaissement est enregistré en brouillon.</div>}
              {erreur && <div className="status-error enc-bandeau" style={{ marginTop: 12 }}>{erreur}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Plus tard (reste en brouillon)</button>
            </div>
          </>
        )}

        {etape === 'attente' && enc && (
          <>
            <div className="modal-body">
              <div className="enc-attente">
                <div className="enc-spinner" />
                <div>En attente du paiement sur <b>{terminalChoisi?.libelle || enc.terminal_libelle || 'le terminal'}</b></div>
                <div className="enc-montant-grand">{fmtEur(enc.montant_cents)}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{enc.client_prenom} {enc.client_nom} — {refLibelle}<br />Le client présente sa carte sur le terminal. Ne fermez pas cette fenêtre.</div>
              </div>
              {erreur && <div className="status-error enc-bandeau">{erreur}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={busy} onClick={annulerTpe}>Annuler la transaction</button>
            </div>
          </>
        )}

        {etape === 'resultat' && enc && (
          <>
            <div className="modal-body">
              <div className="enc-resultat">
                <div className="enc-icone">{enc.statut === 'paye' ? '✅' : enc.statut === 'a_verifier' ? '⏳' : enc.statut === 'brouillon' ? '↩️' : '❌'}</div>
                <h3 style={{ margin: '6px 0' }}>
                  {enc.statut === 'paye' && `Paiement accepté — ${fmtEur(enc.montant_autorise_cents || enc.montant_cents)}`}
                  {enc.statut === 'refuse' && 'Paiement refusé'}
                  {enc.statut === 'annule' && 'Transaction annulée'}
                  {enc.statut === 'a_verifier' && 'Résultat à vérifier'}
                  {enc.statut === 'brouillon' && 'Terminal occupé'}
                </h3>
                {enc.refus_motif && <p style={{ color: 'var(--text-secondary)' }}>{enc.refus_motif}</p>}
                {enc.statut === 'paye' && <p className="enc-ref">Adyen {enc.psp_reference}{enc.carte_resume ? ` · carte ****${enc.carte_resume}` : ''}</p>}
                {enc.statut === 'a_verifier' && <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Le terminal n'a pas répondu à temps. Le CRM l'interroge automatiquement toutes les 2 minutes ; le statut se mettra à jour seul. Ne relancez pas le paiement tant qu'il est « à vérifier ».</p>}
              </div>
            </div>
            <div className="modal-footer">
              {['refuse', 'annule', 'brouillon'].includes(enc.statut) && (
                <button className="btn-secondary" disabled={busy} onClick={async () => {
                  setBusy(true);
                  try {
                    if (enc.statut !== 'brouillon') { const { data } = await api.reouvrir(enc.id); setEnc(data.encaissement); onChange && onChange(data.encaissement); }
                    setEtape('canal');
                  } catch (err) { setErreur(err.response?.data?.error || err.message); } finally { setBusy(false); }
                }}>Réessayer</button>
              )}
              <button className="btn-primary" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}

        {etape === 'lien_ok' && enc && lienResultat && (
          <>
            <div className="modal-body">
              <div className="enc-resultat">
                <div className="enc-icone">🔗</div>
                <h3 style={{ margin: '6px 0' }}>Lien de paiement créé</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Valable jusqu'au {fmtDateHeure(lienResultat.lien?.expiresAt)}. Le statut passera à « Payé » dès que le client aura réglé.</p>
              </div>
              <div className="enc-lien-url">
                <input readOnly value={lienResultat.lien?.url || ''} onFocus={(e) => e.target.select()} style={{ padding: 8, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
                <button className="btn-secondary" onClick={() => copier(lienResultat.lien?.url)}>Copier</button>
              </div>
              <ul style={{ fontSize: 13, marginTop: 12 }}>
                {lienResultat.envois?.sms && <li>SMS : {lienResultat.envois.sms.ok ? '✅ envoyé' : `⚠️ ${lienResultat.envois.sms.erreur}`}</li>}
                {lienResultat.envois?.email && <li>E-mail : {lienResultat.envois.email.ok ? '✅ envoyé' : `⚠️ ${lienResultat.envois.email.erreur}`}</li>}
                {!lienResultat.envois?.sms && !lienResultat.envois?.email && <li>Aucun envoi automatique : transmettez le lien vous-même.</li>}
              </ul>
            </div>
            <div className="modal-footer"><button className="btn-primary" onClick={onClose}>Fermer</button></div>
          </>
        )}
      </div>
    </div>
  );
}

export default EncaissementModal;
